import type { WizardProfile } from '../wizard-state'
import { emptyWriteBacks, type WriteBackIntents } from '../write-back-intents'
import type {
  ExtractedBand,
  ExtractedHeaderModel,
  SuggestionScope,
  TokenKind,
  TokenSuggestion,
} from './types'

// ============================================================
// Token classification: find substrings in extracted lines that
// could resolve from the agency/recruiter profiles. Regex
// detectors + fuzzy matching against saved values. Pure.
//
// Output tiers (see TokenSuggestion in types.ts): exact matches
// auto-link, fuzzy matches ask which form to show, unmatched
// detections ask whose the value is — and saying "the agency's"
// or "a recruiter's" doubles as saving it to that profile.
// ============================================================

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/
// Websites appear in headers with a www./protocol prefix OR as a stylized
// bare domain ("AGENCY.COM") — the bare form is only accepted for a
// conservative TLD list to avoid claiming things like "Inc. Since 1998".
const URL_RE =
  /(?:https?:\/\/)(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/\S*)?|(?:www\.)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/\S*)?|\b[A-Za-z0-9][A-Za-z0-9-]*(?:\.[A-Za-z0-9-]+)*\.(?:com|net|org|io|co|us|biz|info|careers|jobs|agency|staffing)\b(?:\/\S*)?/i
const LINKEDIN_RE = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/\S+/i
const PHONE_RE = /(?:\+?\(?\d[\d\s().-]{7,}\d)/

function normalizePhone(v: string): string {
  return v.replace(/\D/g, '')
}

function normalizeUrl(v: string): string {
  return v
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
}

function normalizeText(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function matchesProfile(
  kind: TokenKind,
  found: string,
  candidate: string | undefined
): boolean {
  if (!candidate?.trim()) return false
  switch (kind) {
    case 'phone': {
      const a = normalizePhone(found)
      const b = normalizePhone(candidate)
      return a.length >= 7 && (a === b || a.endsWith(b) || b.endsWith(a))
    }
    case 'url':
    case 'linkedin':
      return normalizeUrl(found) === normalizeUrl(candidate)
    case 'email':
      return found.trim().toLowerCase() === candidate.trim().toLowerCase()
    case 'name':
      return normalizeText(found) === normalizeText(candidate)
  }
}

/** The saved value a (kind, scope) pair would resolve from. */
export function savedValueFor(
  kind: TokenKind,
  scope: SuggestionScope,
  profile: WizardProfile
): string | undefined {
  if (scope === 'agency') {
    switch (kind) {
      case 'name':
        return profile.agencyName
      case 'phone':
        return profile.agencyContact.phone
      case 'url':
        return profile.agencyContact.url
      case 'email':
        return profile.agencyContact.email
      case 'linkedin':
        return profile.agencyContact.linkedIn
    }
  }
  switch (kind) {
    case 'name':
      return profile.recruiter.name
    case 'phone':
      return profile.recruiter.phone
    case 'email':
      return profile.recruiter.email
    case 'linkedin':
      return profile.recruiter.linkedIn
    case 'url':
      return undefined // there is no {{recruiter.url}}
  }
}

export const KIND_FIELD: Record<TokenKind, string> = {
  email: 'email',
  phone: 'phone',
  url: 'url',
  linkedin: 'linkedIn',
  name: 'name',
}

/** The token a suggestion resolves to under its current choice, if any. */
export function suggestionToken(s: TokenSuggestion): string | null {
  if (s.choice === 'text') return null
  if (s.choice === 'recruiter' && s.kind === 'url') return null
  return `{{${s.choice}.${KIND_FIELD[s.kind]}}}`
}

interface Detection {
  matchedText: string
  kind: TokenKind
  guess: SuggestionScope
  match: TokenSuggestion['match']
  matchedScope?: SuggestionScope
  stylized?: boolean
}

/**
 * Compares a found value against both scopes' saved values.
 * Recruiter wins ties (a value that matches both is more usefully
 * personalized than pinned to the agency).
 */
function matchTier(
  kind: TokenKind,
  found: string,
  profile: WizardProfile
): Pick<Detection, 'match' | 'matchedScope'> {
  for (const scope of ['recruiter', 'agency'] as const) {
    const candidate = savedValueFor(kind, scope, profile)
    if (!matchesProfile(kind, found, candidate)) continue
    return {
      match: found.trim() === candidate?.trim() ? 'exact' : 'fuzzy',
      matchedScope: scope,
    }
  }
  return { match: 'none' }
}

/**
 * Stylized letter-spaced lines ("H I G H C O U N T R Y . C O M") come out
 * of the PDF as one glyph per item with spaces in between — no detector
 * can see a value in that. Mostly single-character tokens = letter-spaced.
 */
function isLetterSpaced(text: string): boolean {
  const tokens = text.trim().split(/\s+/)
  if (tokens.length < 6) return false
  const single = tokens.filter(t => t.length === 1).length
  return single / tokens.length >= 0.7
}

function detectInLine(text: string, profile: WizardProfile): Detection[] {
  // Letter-spaced wordmark: detect against the collapsed text, but claim
  // the ORIGINAL line as the matched span (it reads as one value). Linking
  // such a line would visibly change its rendering — mark it stylized so
  // the default keeps it as written.
  if (isLetterSpaced(text)) {
    return detectInLine(text.replace(/\s+/g, ''), profile).map(d => ({
      ...d,
      matchedText: text.trim(),
      stylized: true,
      match: d.match === 'exact' ? 'fuzzy' : d.match,
    }))
  }

  const out: Detection[] = []
  const { agencyName } = profile

  // LinkedIn before generic URL (a LinkedIn URL matches both patterns).
  const li = text.match(LINKEDIN_RE)?.[0]
  if (li) {
    out.push({
      matchedText: li,
      kind: 'linkedin',
      guess: 'recruiter',
      ...matchTier('linkedin', li, profile),
    })
  }

  const email = text.match(EMAIL_RE)?.[0]
  if (email) {
    out.push({
      matchedText: email,
      kind: 'email',
      guess: 'recruiter',
      ...matchTier('email', email, profile),
    })
  }

  if (!li) {
    const url = text.match(URL_RE)?.[0]
    // The bare-domain form also matches the domain part of an email —
    // skip when the found span lives inside an already-detected email.
    if (url && !EMAIL_RE.test(url) && !(email && email.includes(url))) {
      out.push({
        matchedText: url,
        kind: 'url',
        guess: 'agency',
        ...matchTier('url', url, profile),
      })
    }
  }

  // Phone: exclude spans already claimed by other detections.
  const claimed = out.map(d => d.matchedText)
  const phone = text.match(PHONE_RE)?.[0]
  if (
    phone &&
    normalizePhone(phone).length >= 7 &&
    normalizePhone(phone).length <= 15 &&
    !claimed.some(c => c.includes(phone) || phone.includes(c))
  ) {
    out.push({
      matchedText: phone.trim(),
      kind: 'phone',
      guess: 'recruiter',
      ...matchTier('phone', phone.trim(), profile),
    })
  }

  // Whole-line fallback: headers stylize values in ways the pattern
  // detectors miss (small-caps names, "AGENCY·COM" wordmarks). When a
  // whole line equals one of the user's SAVED values under the field's
  // normalizer, that's a confident match regardless of formatting.
  if (out.length === 0) {
    const line = text.trim()
    const wholeLine: Array<{ kind: TokenKind; guess: SuggestionScope }> = [
      { kind: 'name', guess: 'recruiter' },
      { kind: 'linkedin', guess: 'recruiter' },
      { kind: 'url', guess: 'agency' },
      { kind: 'email', guess: 'recruiter' },
    ]
    for (const w of wholeLine) {
      const tier = matchTier(w.kind, line, profile)
      if (tier.match === 'none') continue
      out.push({ matchedText: line, ...w, ...tier })
      break
    }
  }

  // Name-ish fallback: a line that looks like a person or company name
  // still deserves the whose-question even when it matches nothing saved —
  // documents carry typos and old names, and mapping the line to the live
  // field fixes them for good. Conservative: 1–4 words, each starting
  // with a capital, no digits or @. classifyBands additionally drops these
  // unless the block carries some other detection (a name next to a phone
  // is a signature; a lone capitalized line is usually just a title).
  if (out.length === 0) {
    const line = text.trim()
    const words = line.split(/\s+/)
    const nameLike =
      line.length >= 3 &&
      line.length <= 40 &&
      words.length <= 4 &&
      !/[0-9@]/.test(line) &&
      words.every(w => /^[A-Z]/.test(w))
    if (nameLike) {
      const AGENCY_HINT =
        /agency|staffing|recruit|search|partners|group|talent|consult|\binc\b|\bllc\b/i
      const overlapsAgencyName = Boolean(
        agencyName &&
        (normalizeText(line).includes(normalizeText(agencyName)) ||
          normalizeText(agencyName).includes(normalizeText(line)))
      )
      const isAgency = AGENCY_HINT.test(line) || overlapsAgencyName
      const tier = matchTier('name', line, profile)
      // A partial rendering of the saved agency name ("High Country" for
      // "High Country Staffing") is the same value in a different form:
      // fuzzy, and stylized so the default keeps the document's look.
      const overlapTier =
        tier.match === 'none' && overlapsAgencyName
          ? { match: 'fuzzy' as const, matchedScope: 'agency' as const }
          : tier
      out.push({
        matchedText: line,
        kind: 'name',
        guess: isAgency ? 'agency' : 'recruiter',
        stylized: overlapTier !== tier || undefined,
        ...overlapTier,
      })
    }
  }

  return out
}

/**
 * Default choice per the click-through rule (see TokenSuggestion.choice):
 * exact → linked; fuzzy → linked unless stylized; unmatched → the guessed
 * scope only when that scope has nothing saved yet (the write-back then
 * resolves the token to exactly the found text), otherwise as-written.
 */
function defaultChoice(
  d: Detection,
  profile: WizardProfile
): TokenSuggestion['choice'] {
  if (d.match === 'exact') return d.matchedScope!
  if (d.match === 'fuzzy') return d.stylized ? 'text' : d.matchedScope!
  if (d.kind === 'name') return 'text'
  const saved = savedValueFor(d.kind, d.guess, profile)
  return saved?.trim() ? 'text' : d.guess
}

/** Scans all bands and returns tiered token suggestions with safe defaults. */
export function classifyBands(
  bands: ExtractedBand[],
  profile: WizardProfile
): TokenSuggestion[] {
  const suggestions: TokenSuggestion[] = []
  bands.forEach((band, bandIndex) => {
    band.blocks.forEach((block, blockIndex) => {
      block.lines.forEach((line, lineIndex) => {
        for (const d of detectInLine(line.text, profile)) {
          suggestions.push({
            bandIndex,
            blockIndex,
            lineIndex,
            matchedText: d.matchedText,
            kind: d.kind,
            match: d.match,
            matchedScope: d.matchedScope,
            guess: d.guess,
            stylized: d.stylized,
            choice: defaultChoice(d, profile),
            updateSaved: true,
            saveAsMine: !savedValueFor(d.kind, 'recruiter', profile)?.trim(),
          })
        }
      })
    })
  })

  // Gate the speculative name cards: keep an unmatched name only when its
  // block carries at least one other detection (signature-block shaped).
  return suggestions.filter(s => {
    if (s.kind !== 'name' || s.match !== 'none') return true
    return suggestions.some(
      other =>
        other !== s &&
        other.bandIndex === s.bandIndex &&
        other.blockIndex === s.blockIndex
    )
  })
}

/**
 * The profile write-backs the review step's answers imply. Values matched
 * from saved data need no write; unmatched values write when the user kept
 * the default "save it" resolution (agency: always for empty fields,
 * updateSaved for conflicts; recruiter: saveAsMine).
 */
export function importWriteBackIntents(
  model: ExtractedHeaderModel,
  profile: WizardProfile
): WriteBackIntents {
  const intents = emptyWriteBacks()
  for (const s of model.suggestions) {
    const block = model.bands[s.bandIndex]?.blocks[s.blockIndex]
    if (!block?.included) continue
    if (s.choice === 'text' || s.stylized) continue
    // A value that matched this scope's saved data resolves from it as-is.
    if (s.match !== 'none' && s.matchedScope === s.choice) continue
    const value = s.matchedText.trim()
    const saved = savedValueFor(s.kind, s.choice, profile)?.trim()
    if (s.choice === 'agency') {
      if (s.kind === 'name') continue // never rename the agency from here
      const key = KIND_FIELD[s.kind] as keyof WriteBackIntents['agencyContact']
      if (!saved) intents.agencyContact[key] = value
      else if (saved !== value && s.updateSaved) {
        intents.agencyContact[key] = value
      }
    } else {
      if (s.kind === 'url') continue
      const key = KIND_FIELD[s.kind] as keyof WriteBackIntents['recruiter']
      if (s.saveAsMine && saved !== value) intents.recruiter[key] = value
    }
  }
  return intents
}
